import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import i18n from "i18next";
import type {
  UserData,
  ClientObjective,
  Remark,
  CoachingPackage,
  Invoice,
} from "../pages/coachingNavigator/clientdetails";
import { formatHoursDisplay } from "./formatHours";

const activeLocale = (): string | undefined => i18n.language || undefined;

const formatDate = (date: Date | null): string => {
  if (!date) return "N/A";
  return date.toLocaleDateString(activeLocale());
};

const formatDateTime = (date: Date | null): string => {
  if (!date) return "N/A";
  return date.toLocaleString(activeLocale());
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
};

export const exportClientToPDF = (
  userData: UserData,
  availableMethods: Array<{ id?: string; name: string }>,
  t: (key: string) => string,
  mode: "download" | "show" = "download",
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPosition = 20;

  // Title
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text(t("coachingNavigator.clientOverview.title"), pageWidth / 2, yPosition, {
    align: "center",
  });
  yPosition += 15;

  // Client Name
  doc.setFontSize(16);
  doc.text(userData.name || "N/A", pageWidth / 2, yPosition, {
    align: "center",
  });
  yPosition += 12;

  // Key Information Section
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(t("coachingNavigator.clientOverview.keyInformation"), 14, yPosition);
  yPosition += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  const keyInfoData = [
    [
      t("coachingNavigator.clientOverview.company"),
      userData.company || "N/A",
    ],
    [t("coachingNavigator.clientOverview.role"), userData.role || "N/A"],
    [t("coachingNavigator.clientOverview.email"), userData.email || "N/A"],
    [
      t("coachingNavigator.clientOverview.linkedIn"),
      userData.linkedIn || "N/A",
    ],
    [
      t("coachingNavigator.clientOverview.dateOfBirth"),
      formatDate(userData.dateOfBirth),
    ],
    [
      t("coachingNavigator.clientOverview.homeLocation"),
      userData.wohnort || "N/A",
    ],
    [
      t("coachingNavigator.clientOverview.workLocation"),
      userData.arbeitsort || "N/A",
    ],
  ];

  autoTable(doc, {
    startY: yPosition,
    head: [],
    body: keyInfoData,
    theme: "plain",
    styles: { fontSize: 10, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 50 },
      1: { cellWidth: "auto" },
    },
  });

  yPosition = (doc as any).lastAutoTable.finalY + 10;

  // Coaching Hours Summary
  const totalBooked = userData.coachingPackages.reduce(
    (sum, pkg) => sum + (pkg.hoursBooked || 0),
    0,
  );
  const totalCompleted = userData.completedHours || 0;
  const totalRemaining = totalBooked - totalCompleted;

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(t("coachingNavigator.clientOverview.hoursSummary"), 14, yPosition);
  yPosition += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  const hoursSummary = [
    [
      t("coachingNavigator.clientOverview.totalBooked"),
      formatHoursDisplay(totalBooked),
    ],
    [
      t("coachingNavigator.clientOverview.totalCompleted"),
      formatHoursDisplay(totalCompleted),
    ],
    [
      t("coachingNavigator.clientOverview.totalRemaining"),
      formatHoursDisplay(totalRemaining),
    ],
  ];

  autoTable(doc, {
    startY: yPosition,
    head: [],
    body: hoursSummary,
    theme: "plain",
    styles: { fontSize: 10, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 50 },
      1: { cellWidth: "auto" },
    },
  });

  yPosition = (doc as any).lastAutoTable.finalY + 10;

  // Check if we need a new page
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  // Client Objectives
  if (userData.clientObjectives.length > 0) {
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(
      t("coachingNavigator.clientOverview.clientObjectives"),
      14,
      yPosition,
    );
    yPosition += 8;

    const objectivesData = userData.clientObjectives.map((obj) => [
      formatDateTime(obj.timestamp),
      obj.content,
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [
        [
          t("coachingNavigator.clientOverview.date"),
          t("coachingNavigator.clientOverview.objective"),
        ],
      ],
      body: objectivesData,
      theme: "striped",
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [66, 66, 66], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: "auto" },
      },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;
  }

  // Check if we need a new page
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  // Further Remarks
  if (userData.remarks.length > 0) {
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(
      t("coachingNavigator.clientOverview.furtherRemarks"),
      14,
      yPosition,
    );
    yPosition += 8;

    const remarksData = userData.remarks.map((remark) => [
      formatDateTime(remark.timestamp),
      remark.content,
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [
        [
          t("coachingNavigator.clientOverview.date"),
          t("coachingNavigator.clientOverview.remark"),
        ],
      ],
      body: remarksData,
      theme: "striped",
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [66, 66, 66], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: "auto" },
      },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;
  }

  // Check if we need a new page
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  // Methods Used
  if (userData.methodsUsed.length > 0) {
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(t("coachingNavigator.clientOverview.methodsUsed"), 14, yPosition);
    yPosition += 8;

    const methodNames = userData.methodsUsed
      .map((methodId) => {
        const method = availableMethods.find(
          (m) => m.id === methodId || m.name === methodId,
        );
        return method?.name || methodId;
      })
      .join(", ");

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const splitMethods = doc.splitTextToSize(methodNames, pageWidth - 28);
    doc.text(splitMethods, 14, yPosition);
    yPosition += splitMethods.length * 5 + 5;
  }

  // Check if we need a new page
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  // Personal Attributes
  const filledAttributes = Object.entries(userData.personalAttributes).filter(
    ([, value]) => value !== null,
  );

  if (filledAttributes.length > 0) {
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(
      t("coachingNavigator.clientOverview.personalInformation"),
      14,
      yPosition,
    );
    yPosition += 8;

    const attributesData = filledAttributes.map(([key, value]) => [
      key,
      value === true ? "✓" : value === false ? "✗" : "—",
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [
        [
          t("coachingNavigator.clientOverview.attribute"),
          t("coachingNavigator.clientOverview.value"),
        ],
      ],
      body: attributesData,
      theme: "striped",
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [66, 66, 66], textColor: 255 },
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { cellWidth: 30, halign: "center" },
      },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;
  }

  // Check if we need a new page
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  // Coaching Packages
  if (userData.coachingPackages.length > 0) {
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(
      t("coachingNavigator.clientOverview.coachingPackages"),
      14,
      yPosition,
    );
    yPosition += 8;

    const packagesData = userData.coachingPackages.map((pkg) => [
      formatDate(pkg.startDate),
      formatDate(pkg.endDate),
      formatHoursDisplay(pkg.hoursBooked),
      pkg.comment || "—",
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [
        [
          t("coachingNavigator.clientOverview.startDate"),
          t("coachingNavigator.clientOverview.endDate"),
          t("coachingNavigator.clientOverview.hours"),
          t("coachingNavigator.clientOverview.comment"),
        ],
      ],
      body: packagesData,
      theme: "striped",
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [66, 66, 66], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 30 },
        2: { cellWidth: 25 },
        3: { cellWidth: "auto" },
      },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;
  }

  // Check if we need a new page
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }

  // Invoices
  if (userData.invoices.length > 0) {
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(t("coachingNavigator.clientOverview.invoices"), 14, yPosition);
    yPosition += 8;

    const invoicesData = userData.invoices.map((inv) => [
      formatDate(inv.dateSent),
      formatCurrency(inv.amount),
      inv.isPaid ? "✓" : "✗",
      inv.remarks || "—",
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [
        [
          t("coachingNavigator.clientOverview.dateSent"),
          t("coachingNavigator.clientOverview.amount"),
          t("coachingNavigator.clientOverview.paid"),
          t("coachingNavigator.clientOverview.remarks"),
        ],
      ],
      body: invoicesData,
      theme: "striped",
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [66, 66, 66], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 30 },
        2: { cellWidth: 20, halign: "center" },
        3: { cellWidth: "auto" },
      },
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;
  }

  // Footer on all pages
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(150);
    doc.text(
      `${t("coachingNavigator.clientOverview.generatedOn")}: ${new Date().toLocaleString(activeLocale())}`,
      14,
      doc.internal.pageSize.getHeight() - 10,
    );
    doc.text("PDF generated by Pinnacle (Coaching Hub)", pageWidth / 2, doc.internal.pageSize.getHeight() - 10, {
      align: "center",
    });
    doc.text(
      `${t("coachingNavigator.clientOverview.page")} ${i} ${t("coachingNavigator.clientOverview.of")} ${pageCount}`,
      pageWidth - 14,
      doc.internal.pageSize.getHeight() - 10,
      { align: "right" },
    );
  }

  const fileName = `client-${userData.name.replace(/\s+/g, "-").toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`;
  if (mode === "show") {
    window.open(doc.output("bloburl"), "_blank");
  } else {
    doc.save(fileName);
  }
};
